import { useState } from "react";
import { C, FONT, acGlow, btnPrimary, logoDot, sectionLabel } from "../theme";
import { useMediaQuery, MOBILE_QUERY } from "../lib/useMediaQuery";
import { Dots } from "../components/Dots";
import { CodeTabs } from "../components/CodeTabs";
import { demoSearch } from "../lib/api";
import {
  DEFAULT_PERIOD,
  PERIOD_ORDER,
  PRICING,
  planTotal,
  type Period,
} from "../lib/pricing";
import { isFeatureEnabled } from "../lib/featureFlags";

// TEMPORARY (awaiting tax ID): while the `subscribe-buttons` flag is OFF, the
// subscribe CTAs are hidden (kept in the DOM) and this "contact us" link shows
// in their place. Toggle with `featureFlags.on('subscribe-buttons')` on dev.
const CONTACT_HREF = "mailto:contact@zynday.com";

const EXAMPLES = {
  curl: `curl "https://geo.zynday.com/v1/search?q=Berlin&format=json" \\
  -H "Authorization: Bearer zk_live_8f3a…"`,
  js: `const res = await fetch(
  "https://geo.zynday.com/v1/search?" +
    new URLSearchParams({ q: "Berlin", format: "json" }),
  { headers: { Authorization: "Bearer zk_live_8f3a…" } }
);
const data = await res.json();`,
  py: `import requests

r = requests.get(
    "https://geo.zynday.com/v1/search",
    params={"q": "Berlin", "format": "json"},
    headers={"Authorization": "Bearer zk_live_8f3a…"},
)
print(r.json())`,
};

const FEATURES = [
  "Unlimited requests, no extra charges",
  "Look up addresses and locations",
  "Up to 20 requests per second",
  "Regularly updated location data",
  "Up to 5 named API keys",
  "Passwordless login · cancel anytime",
];

const dashLine = `1px dashed ${C.line2}`;

export function Landing({
  onLogin,
  onSubscribe,
}: {
  onLogin: () => void;
  onSubscribe: (p: Period) => void;
}) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const showSubscribe = isFeatureEnabled("subscribe-buttons");
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const [demoQuery, setDemoQuery] = useState(
    "1600 Amphitheatre Parkway, Mountain View",
  );
  const [demoResult, setDemoResult] = useState("");
  const [demoLoading, setDemoLoading] = useState(false);

  const plan = PRICING[period];

  const runDemo = async () => {
    setDemoLoading(true);
    setDemoResult(await demoSearch(demoQuery));
    setDemoLoading(false);
  };

  return (
    <div data-screen-label="Landing">
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "14px 16px" : "16px 32px",
          borderBottom: `1px solid ${C.line}`,
          position: "sticky",
          top: 0,
          background: C.bg,
          zIndex: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 11, fontSize: 13, color: C.muted, minWidth: 0 }}>
          {!isMobile && <span style={{ color: C.mutedDim }}>GEO(1)</span>}
          <span style={{ color: C.textBright, fontWeight: 700, display: "flex", alignItems: "center", gap: 9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <span style={{ ...logoDot, flex: "none" }} />geo.zynday.com
          </span>
        </div>
        <nav style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 24, fontSize: 13, color: C.muted, flex: "none" }}>
          {!isMobile && <a href="#api" style={{ cursor: "pointer", textDecoration: "none" }}>docs</a>}
          {!isMobile && <a href="#pricing" style={{ cursor: "pointer", textDecoration: "none" }}>pricing</a>}
          <button onClick={onLogin} style={{ background: "transparent", border: `1px solid ${C.line3}`, color: C.ac, padding: "8px 16px", borderRadius: 4, font: `600 13px ${FONT}`, cursor: "pointer" }}>
            log in
          </button>
          {!showSubscribe && (
            <a href={CONTACT_HREF} style={{ background: C.ac, color: C.acInk, padding: "8px 16px", borderRadius: 4, font: `700 13px ${FONT}`, cursor: "pointer", boxShadow: acGlow(0.3), textDecoration: "none", display: "inline-block" }}>
              contact us
            </a>
          )}
          <button onClick={() => onSubscribe(period)} style={{ background: C.ac, border: "none", color: C.acInk, padding: "8px 16px", borderRadius: 4, font: `700 13px ${FONT}`, cursor: "pointer", boxShadow: acGlow(0.3), display: showSubscribe ? undefined : "none" }}>
            subscribe
          </button>
        </nav>
      </header>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 32px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", color: C.mutedDim, fontSize: 12, padding: "16px 0", borderBottom: dashLine }}>
          GEO(1)<span>General Commands Manual</span>GEO(1)
        </div>

        {/* NAME */}
        <section style={{ padding: "64px 0 8px" }}>
          <div style={sectionLabel}>NAME</div>
          <h1 style={{ fontSize: isMobile ? 28 : 42, lineHeight: 1.18, color: C.textBrightest, margin: "0 0 18px", fontWeight: 700, maxWidth: 780, textWrap: "balance" }}>
            geo.zynday.com — find the location of any address,{!isMobile && <br />} and the address of any location.
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: C.mutedSoft, margin: 0, maxWidth: 600 }}>
            A simple location lookup at a flat monthly price. Send an address and
            get back its point on the map — or send a point and get back the
            address. Unlimited requests, no per-request charges.
          </p>
        </section>

        {/* SYNOPSIS */}
        <section style={{ padding: "48px 0", borderTop: dashLine, marginTop: 48 }} id="api">
          <div style={sectionLabel}>SYNOPSIS</div>
          <div style={{ background: C.panel, border: `1px solid ${C.line2}`, borderRadius: 8, padding: "22px 24px", fontSize: 14, lineHeight: 2, color: C.text, marginBottom: 24, overflowWrap: "anywhere" }}>
            <div><span style={{ color: C.muted }}>GET</span> <span style={{ color: C.textBright }}>https://geo.zynday.com/v1/search</span>?q=<span style={{ color: C.ac }}>&lt;query&gt;</span>&amp;format=json</div>
            <div><span style={{ color: C.muted }}>GET</span> <span style={{ color: C.textBright }}>https://geo.zynday.com/v1/reverse</span>?lat=<span style={{ color: C.ac }}>&lt;lat&gt;</span>&amp;lon=<span style={{ color: C.ac }}>&lt;lon&gt;</span></div>
            <div style={{ color: C.mutedDim, borderTop: `1px solid ${C.line}`, marginTop: 12, paddingTop: 12 }}>Authorization: Bearer &lt;your-api-key&gt;</div>
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            {!showSubscribe && (
              <a href={CONTACT_HREF} style={{ ...btnPrimary, padding: "14px 26px", textDecoration: "none", display: "inline-block" }}>
                contact us
              </a>
            )}
            <button onClick={() => onSubscribe(period)} style={{ ...btnPrimary, padding: "14px 26px", display: showSubscribe ? undefined : "none" }}>
              subscribe — €{plan.perMo}/mo <Dots />
            </button>
            <button onClick={onLogin} style={{ background: "transparent", border: `1px solid ${C.line3}`, color: C.text, padding: "14px 26px", borderRadius: 5, font: `600 14px ${FONT}`, cursor: "pointer" }}>
              log in
            </button>
          </div>
        </section>

        {/* DESCRIPTION */}
        <section style={{ padding: "48px 0", borderTop: dashLine }}>
          <div style={sectionLabel}>DESCRIPTION</div>
          <p style={{ fontSize: 15, lineHeight: 1.8, color: C.mutedSoft, margin: "0 0 28px", maxWidth: 680 }}>
            geo.zynday.com gives you one URL and one key. Send an address and get
            its point on the map; send a point and get the nearest address. We run
            and look after the servers — you just send requests. One flat price,
            no usage meters, no surprise bills.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 1, background: C.line, border: `1px solid ${C.line}`, borderRadius: 8, overflow: "hidden" }}>
            {[
              ["--unlimited", "No request caps", "Flat price. Never a metered bill."],
              ["--burst", "Up to 20 / second", "Plenty for busy apps."],
              ["--fresh", "Always current", "Location data updated automatically."],
            ].map(([flag, title, body]) => (
              <div key={flag} style={{ background: C.bg, padding: 24 }}>
                <div style={{ color: C.ac, fontSize: 13, marginBottom: 9 }}>{flag}</div>
                <div style={{ color: C.textBright, fontWeight: 600, marginBottom: 6, fontSize: 14 }}>{title}</div>
                <div style={{ color: C.muted, fontSize: 12.5, lineHeight: 1.6 }}>{body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* TRY IT */}
        <section style={{ padding: "48px 0", borderTop: dashLine }}>
          <div style={{ ...sectionLabel, marginBottom: 6 }}>TRY IT</div>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 18px" }}>
            Try a live lookup — type any place and run.
          </p>
          <div style={{ background: C.panel, border: `1px solid ${C.line2}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderBottom: `1px solid ${C.line}`, background: C.panelAlt }}>
              {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                <span key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />
              ))}
              <span style={{ marginLeft: 10, color: C.mutedDim, fontSize: 12 }}>GET /v1/search</span>
            </div>
            <div style={{ padding: 18 }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center" }}>
                <span style={{ color: C.ac, fontSize: 13 }}>$</span>
                <input
                  value={demoQuery}
                  onChange={(e) => setDemoQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runDemo()}
                  placeholder="search any place…"
                  style={{ flex: 1, background: C.bg, border: `1px solid ${C.line3}`, borderRadius: 6, padding: "11px 13px", color: C.textBrightest, font: `500 13px ${FONT}` }}
                />
                <button onClick={runDemo} style={{ background: C.ac, border: "none", color: C.acInk, padding: "11px 22px", borderRadius: 6, font: `700 13px ${FONT}`, cursor: "pointer" }}>
                  {demoLoading ? "…" : "run"}
                </button>
              </div>
              <div style={{ background: C.inset, border: `1px solid ${C.line}`, borderRadius: 6, padding: 16, maxHeight: 280, overflow: "auto" }}>
                <pre style={{ margin: 0, fontFamily: "inherit", fontSize: 12.5, lineHeight: 1.75, color: "#7fd1a8", whiteSpace: "pre-wrap" }}>
                  {demoResult || "// press run to query"}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section style={{ padding: "48px 0", borderTop: dashLine }}>
          <div style={sectionLabel}>EXAMPLES</div>
          <CodeTabs snippets={EXAMPLES} />
        </section>

        {/* PRICING */}
        <section style={{ padding: "48px 0", borderTop: dashLine }} id="pricing">
          <div style={sectionLabel}>PRICING</div>
          <div style={{ display: "flex", gap: 10, marginBottom: 28, flexWrap: "wrap" }}>
            {PERIOD_ORDER.map((k) => {
              const active = k === period;
              const info = PRICING[k];
              return (
                <div
                  key={k}
                  onClick={() => setPeriod(k)}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    cursor: "pointer",
                    textAlign: "center",
                    background: active ? "rgba(54,224,127,.06)" : C.panel,
                    border: active ? `1px solid ${C.ac}` : `1px solid ${C.line2}`,
                    borderRadius: 8,
                    padding: "14px 10px",
                    boxShadow: active ? "0 0 18px rgba(54,224,127,.14)" : "none",
                  }}
                >
                  <div style={{ color: active ? C.textBrightest : C.text, fontSize: 13, fontWeight: 700, marginBottom: 5 }}>{info.label}</div>
                  <div style={{ color: C.muted, fontSize: 12 }}>€{info.perMo}/mo</div>
                  {info.save && <div style={{ color: C.ac, fontSize: 11, marginTop: 7, fontWeight: 700 }}>{info.save}</div>}
                </div>
              );
            })}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 32, alignItems: "center" }}>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 8 }}>
                <span style={{ color: C.textBrightest, fontSize: 52, fontWeight: 700 }}>€{plan.perMo}</span>
                <span style={{ color: C.muted, fontSize: 15 }}>/ month</span>
              </div>
              <div style={{ color: C.mutedDim, fontSize: 13, marginBottom: 18 }}>{plan.billed} · via Stripe · cancel anytime</div>
              <div style={{ color: C.mutedSoft, fontSize: 14, lineHeight: 1.7, marginBottom: 22 }}>
                Pro — everything included. Longer commitments lock in a lower
                monthly rate; month-to-month stays flexible.
              </div>
              {!showSubscribe && (
                <a href={CONTACT_HREF} style={{ ...btnPrimary, padding: "14px 28px", textDecoration: "none", display: "inline-block" }}>contact us</a>
              )}
              <button onClick={() => onSubscribe(period)} style={{ ...btnPrimary, padding: "14px 28px", display: showSubscribe ? undefined : "none" }}>subscribe now <Dots /></button>
              <div style={{ color: C.mutedDim, fontSize: 12, marginTop: 12 }}>Total today: €{planTotal(period)}.00</div>
            </div>
            <div style={{ background: C.panel, border: `1px solid ${C.line3}`, borderRadius: 10, padding: 28, display: "flex", flexDirection: "column", gap: 13, fontSize: 13.5, color: C.soft }}>
              {FEATURES.map((f) => (
                <div key={f}><span style={{ color: C.ac }}>✓</span> {f}</div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <footer style={{ borderTop: `1px solid ${C.line}`, padding: "26px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", color: C.mutedDim, fontSize: 12, maxWidth: 900, margin: "0 auto", flexWrap: "wrap", gap: 12 }}>
        <span>© 2026 zynday · data © <a href="https://www.openstreetmap.org/copyright" style={{ color: C.muted, textDecoration: "underline" }}>OpenStreetMap contributors</a></span>
        <span style={{ display: "flex", gap: 16 }}>
          <a href="/terms" style={{ color: C.muted, textDecoration: "underline" }}>terms</a>
          <a href="/privacy" style={{ color: C.muted, textDecoration: "underline" }}>privacy</a>
          <a href="/imprint" style={{ color: C.muted, textDecoration: "underline" }}>imprint</a>
        </span>
      </footer>
    </div>
  );
}
